import { useMemo } from 'react'

import {
  HourglassIcon,
  ShoppingCartCheckOut01Icon,
  StoreRemove02Icon
} from '@hugeicons/react'
import _ from 'lodash'
import Countdown from 'react-countdown'

import { useBottomSheetContext, withBottomSheet } from '@/shared/contexts/BottomSheetContext'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useAbilityContext } from '@/shared/hooks'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { useRewardsStore } from '@/shared/store/useRewardsStore'
import { Button } from '@/shared/ui/Button'
import { EmptyAlert } from '@/shared/ui/EmptyAlert'
import { Resource } from '@/shared/ui/Resource'
import {
  PageHeader,
  ResourceActiveFilterChips,
  ResourceFilterBar,
  Screen
} from '@/shared/ui/Layout'

import { DigitalProductStoreAlerts } from './Alerts'
import { StoreStatusPanel } from './Alerts/StoreStatusPanel'
import { DigitalProductStoreCard } from './Card'
import { StoreAdvancedFilter } from './components/StoreAdvancedFilter'
import { StoreCatalogFilterPills } from './components/StoreCatalogFilterPills'
import { StoreClosesBanner } from './components/StoreClosesBanner'
import { useStoreSheetFromUrl } from './hooks/useStoreSheetFromUrl'
import { useStoreListFilters } from './hooks/useStoreListFilters'
import { useDigitalProductStore } from './hook'
import { buildStoreItemSheetHref } from './lib/storeSheetSearch'
import { canRenderStoreCatalog } from './lib/storeHours'
import { resolveRewardPartner } from './lib/resolveItemPartner'
import { resolveItemRewardCategory } from './lib/resolveItemRewardCategory'
import { DigitalProductStoreWithdrawBottomSheet } from './Withdraw/BottomSheet'

const formatAdminCountdown = ({ days, hours, minutes, seconds, completed }) => {
  if (completed) return null

  const parts = []

  if (days > 0) {
    parts.push(`${days}d`)
    parts.push(`${hours}h`)
  } else if (hours > 0) {
    parts.push(`${hours}h`)
  }

  parts.push(`${minutes}min`)
  parts.push(`${seconds}seg`)

  return parts.join(' ')
}

export const DigitalProductStore = withBottomSheet(({ sheetSearch = {} }) => {
  const router = useAppRouter()
  const { showBottomSheet } = useBottomSheetContext()

  useStoreSheetFromUrl({ sheetSearch })

  const {
    digitalProductId,
    digitalProduct,
    refetch: refetchDigitalProduct
  } = useDigitalProductContext()

  const ability = useAbilityContext()
  const canManageAll = !!ability?.can?.('manage', 'all')

  const {
    storeStatus,
    willOpenSoon,
    alreadyClosed,
    isAvailable,
    openingHours
  } = useDigitalProductStore()

  const storeWillOpenSoon = willOpenSoon()
  const storeAlreadyClosed = alreadyClosed()
  const shouldRenderRewards = canRenderStoreCatalog(storeStatus, {
    canManageAll
  })
  const showParticipantClosedState =
    !canManageAll &&
    (storeStatus === 'loading' ||
      storeStatus === 'soon' ||
      storeStatus === 'closed')

  const storeIsOpen = storeStatus === 'open'

  const storeBanners = digitalProduct?.store_setting?.store_banners ?? []

  const { rewardItems, rewardRequests, refetchRewardRequests, config: { humanName } } =
    useRewardsStore()

  const { wallet, userProgress } = useUserProgress()
  const walletReady = Boolean(userProgress?.wallet)
  const walletCoins = wallet?.coins

  const partners = useGamificationDataStore(
    state => state.getActiveProductData().partners
  )

  const partnerNameById = useMemo(
    () =>
      Object.fromEntries(
        partners.map(partner => [String(partner.id), partner.name])
      ),
    [partners]
  )

  const filters = useStoreListFilters({
    rewardItems,
    isAvailable,
    walletCoins,
    walletReady,
    partnerNameById
  })

  const getRewardRequestsForRewardItem = (rewardItemId, statuses) =>
    rewardRequests.filter(
      request =>
        request.reward_item_id == rewardItemId &&
        _.includes(statuses, request.status)
    )

  const handleShow = rewardItem => {
    router.push(
      buildStoreItemSheetHref(rewardItem.reward_id, rewardItem.id)
    )
  }

  const handleShowWithdraw = () => {
    const filteredRequests = rewardRequests.filter(
      request => request.status === 'approved'
    )

    showBottomSheet({
      component: DigitalProductStoreWithdrawBottomSheet,
      rewardRequests: filteredRequests,
      digitalProductId,
      refetchRewardRequests
    })
  }

  const renderAdminStatus = () => {
    if (!canManageAll || storeStatus === 'open' || storeStatus === 'unscheduled') {
      return null
    }

    if (storeWillOpenSoon && openingHours.openAt?.isValid) {
      return (
        <div className="mb-4 rounded-xl border border-brand/15 bg-brand/10 px-4 py-3">
          <div className="flex items-center gap-2 font-semibold text-brand">
            <HourglassIcon size={18} aria-hidden />
            <span>
              Lojinha abre em{' '}
              <Countdown
                date={openingHours.openAt.toJSDate()}
                zeroPadTime={2}
                renderer={formatAdminCountdown}
              />
            </span>
          </div>
          <p className="m-0 mt-1.5 text-xs font-semibold leading-snug text-gray-600">
            Pré-visualização do catálogo para a equipe. Participantes só verão
            os itens quando a lojinha abrir.
          </p>
        </div>
      )
    }

    if (storeAlreadyClosed) {
      return (
        <div className="mb-4 rounded-xl border border-gray-200 bg-gray-100 px-4 py-3">
          <div className="flex items-center gap-2 font-semibold text-gray-800">
            <StoreRemove02Icon size={18} aria-hidden />
            <span>Lojinha fechada para participantes</span>
          </div>
          <p className="m-0 mt-1.5 text-xs font-semibold leading-snug text-gray-600">
            O catálogo segue visível para você em modo de pré-visualização.
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <Screen>
      <Screen.Context>
        <PageHeader
          title={humanName.plural}
          actions={
            <Button
              variant="primary"
              icon
              size="md"
              leadingIcon={<ShoppingCartCheckOut01Icon />}
              aria-label="Retiradas"
              onClick={handleShowWithdraw}
            />
          }
        />
      </Screen.Context>

      {shouldRenderRewards ? (
        <Screen.Sticky className="flex flex-col gap-2">
          <ResourceFilterBar
            searchValue={filters.searchInput}
            onSearchChange={filters.setSearchInput}
            onSearchSubmit={filters.flushSearch}
            searchPlaceholder="Buscar recompensa..."
            searchAriaLabel="Buscar recompensa"
            advancedFilter={
              filters.showPartnerAdvancedFilter ? (
                <StoreAdvancedFilter
                  filters={filters.partnerFilters}
                  partnerOptions={filters.partnerOptions}
                  categoryOptions={filters.categoryOptions}
                  hasActiveFilters={filters.hasActivePartnerAdvanced}
                  onApplyDraft={filters.applyPartnerDraft}
                  onReset={filters.resetPartnerAdvanced}
                  className="shrink-0"
                />
              ) : undefined
            }
          />

          <StoreCatalogFilterPills
            available={filters.advancedFilters.available}
            soldOut={filters.advancedFilters.soldOut}
            requested={filters.advancedFilters.requested}
            affordable={filters.advancedFilters.affordable}
            walletReady={walletReady}
            onToggleAvailable={filters.toggleAvailable}
            onToggleSoldOut={filters.toggleSoldOut}
            onToggleRequested={filters.toggleRequested}
            onToggleAffordable={filters.toggleAffordable}
          />

          <ResourceActiveFilterChips
            chips={filters.activeChips}
            onRemove={filters.removeChip}
          />
        </Screen.Sticky>
      ) : null}

      <Screen.Content className="pt-2">
        {/* Par simétrico do `Alerts/OpenSoon`, que conta até `openAt` e refaz a
            busca no `soon→open`. Sem este, quem atravessa o fechamento com a
            tela aberta continua vendo catálogo, botão de resgate e o prazo já
            vencido: a busca do evento tem `staleTime` de 5 min e não faz poll.

            A `key` é o instante do fechamento — uma busca refeita que devolva
            a mesma janela não remonta o contador, e ele não redispara. */}
        {storeIsOpen && openingHours.closeAt?.isValid ? (
          <Countdown
            key={openingHours.closeAt.toMillis()}
            date={openingHours.closeAt.toJSDate()}
            renderer={() => null}
            onComplete={refetchDigitalProduct}
          />
        ) : null}

        {renderAdminStatus()}

        <StoreClosesBanner banners={storeBanners} />

        {showParticipantClosedState ? (
          <DigitalProductStoreAlerts>
            {storeStatus === 'loading' ? (
              <StoreStatusPanel
                icon="shop"
                badge="Carregando"
                title={
                  <h2 className="m-0 text-xl font-extrabold leading-snug text-gray-900">
                    Verificando horário da lojinha
                  </h2>
                }
                description="Aguarde um instante enquanto confirmamos quando as reservas estarão disponíveis."
                footer={
                  <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-700">
                    <span
                      className="inline-block size-4 animate-spin rounded-full border-2 border-brand border-t-transparent"
                      aria-hidden
                    />
                    Carregando
                  </div>
                }
              />
            ) : null}

            {storeWillOpenSoon ? (
              <DigitalProductStoreAlerts.OpenSoon
                onOpen={refetchDigitalProduct}
              />
            ) : null}

            {storeAlreadyClosed ? (
              <DigitalProductStoreAlerts.AlreadyClosed
                refetchStore={refetchDigitalProduct}
              />
            ) : null}
          </DigitalProductStoreAlerts>
        ) : null}

        {/* A vitrine sem item nenhum precisa dizer isso. O grid vazio deixava
            cabeçalho, filtros e faixa de aviso com um buraco embaixo, e buraco
            lê como defeito — inclusive quando é o Produtor que escondeu tudo
            de propósito pelo painel.

            A mensagem não distingue a causa, e não é omissão: o participante
            não tem como saber, e não é assunto dele. Item escondido pelo
            interruptor e catálogo ainda não montado chegam aqui iguais. */}
        {shouldRenderRewards && filters.filteredRewardItems.length === 0 ? (
          <div className="mb-8">
            <EmptyAlert
              title={
                rewardItems.length === 0
                  ? 'Nenhum item na lojinha por enquanto'
                  : 'Nenhum item com esses filtros'
              }
              subtitle={
                rewardItems.length === 0
                  ? 'Assim que a organização publicar as recompensas, elas aparecem aqui.'
                  : 'Tente limpar os filtros para ver o restante da lojinha.'
              }
            />
          </div>
        ) : null}

        {/* A região fica montada mesmo vazia: live region criada junto com o
            texto não chega a ser anunciada, porque o leitor de tela precisa já
            estar observando o nó quando o conteúdo entra. Mesmo padrão de
            `shared/ui/Layout/chrome/RewardGainBadgeHost.tsx`. */}
        {shouldRenderRewards ? (
          <div role="status" aria-live="polite">
            {filters.advancedFilters.soldOut && !filters.hasSoldOutItems ? (
              <p className="m-0 mb-4 text-base leading-relaxed text-gray-700">
                Nenhum item esgotado neste evento.
              </p>
            ) : null}
          </div>
        ) : null}

        {shouldRenderRewards ? (
          <div className="mb-8 grid grid-cols-12 gap-4">
            {filters.filteredRewardItems.map(rewardItem => {
              const rewardRequestsForThisReward = getRewardRequestsForRewardItem(
                rewardItem.id,
                ['approved', 'delivered']
              )
              const partner = resolveRewardPartner(rewardItem, partnerNameById)
              const rewardCategory = resolveItemRewardCategory(rewardItem)
              // O check-in exigido é do parceiro do reward, com fallback pro
              // parceiro do item — mesma prioridade de `EligibilityService#checkin_missing?`
              // na api. O badge (`partner` acima) só tem id/label; o redirect
              // precisa do slug, então lê do objeto crú.
              const checkinPartner = rewardItem.reward?.partner || rewardItem.partner

              return (
                <div key={rewardItem.id} className="col-span-6 xl:col-span-4">
                  <Resource.Root type="RewardItem" id={rewardItem.id}>
                    <DigitalProductStoreCard
                      {...rewardItem}
                      reward_category={rewardCategory}
                      checkinPartner={checkinPartner}
                      partner={partner}
                      rewardRequestsCount={rewardRequestsForThisReward.length}
                      onShow={() => handleShow(rewardItem)}
                    />
                  </Resource.Root>
                </div>
              )
            })}
          </div>
        ) : null}
      </Screen.Content>
    </Screen>
  )
})
